import { styled } from '@linaria/react';
import {
  blur,
  colors,
  component,
  fonts,
  layout,
  semantic,
  space,
  textStyle,
} from '../../theme/tokens';

export const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
`;

export const StyledButton = styled.button<{
  $fullWidth: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${textStyle.body.lg.strong.fontFamily};
  font-weight: ${textStyle.body.lg.strong.fontWeight};
  letter-spacing: ${textStyle.body.lg.strong.letterSpacing};
  line-height: ${textStyle.body.lg.strong.lineHeight};
  border-radius: ${layout.borderRadiusFull};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};

  padding: ${space[3]} ${space['20px']};
  font-size: ${textStyle.body.lg.strong.fontSize};

  &[data-size='small'] {
    padding: ${space[2]} ${space[4]};
    font-size: ${textStyle.body.md.strong.fontSize};
  }

  &[data-size='large'] {
    padding: ${space[4]} ${space[5]};
    font-size: ${textStyle.body.lg.strong.fontSize};
  }

  background-color: ${component.button.primary.enabled.bg};
  color: ${component.button.primary.enabled.text};
  border: 2px solid ${component.button.primary.enabled.bg};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  &[data-variant='primary'] {
    &:disabled {
      background-color: ${component.button.primary.disabled.bg};
      color: ${component.button.primary.disabled.text};
      border-color: ${component.button.primary.disabled.bg};
      opacity: 1;
    }

    &:focus-visible {
      background-color: ${component.button.primary.focussed.bg};
      color: ${component.button.primary.focussed.text};
    }

    &:hover:not(:disabled) {
      background-color: ${component.button.primary.hover.bg};
      border-color: ${component.button.primary.hover.bg};
      color: ${component.button.primary.hover.text};
    }

    &:active:not(:disabled) {
      background-color: ${component.button.primary.pressed.bg};
      border-color: ${component.button.primary.pressed.bg};
      color: ${component.button.primary.pressed.text};
    }
  }

  &:not([data-variant='primary']):hover:not(:disabled) {
    opacity: 0.85;
  }

  &[data-variant='secondary'] {
    background-color: ${semantic.bg.secondary};
    color: ${semantic.text.primary};
    border: 2px solid transparent;

    &:focus-visible {
      background-color: ${semantic.bg.surfaceStrong};
      color: ${semantic.text.primary};
    }

    &:disabled {
      background-color: ${semantic.bg.surfaceSubtle};
      color: ${semantic.text.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
    }
  }

  &[data-variant='outline'] {
    background-color: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
    border: 2px solid transparent;

    &:focus-visible {
      background-color: ${semantic.bg.surfaceStrong};
      color: ${semantic.text.primary};
    }

    &:disabled {
      background-color: ${semantic.bg.surfaceSubtle};
      color: ${semantic.text.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.surfaceStrong};
      color: ${semantic.text.primary};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
    }
  }

  &[data-variant='toggle'] {
    background-color: transparent;
    color: ${semantic.text.primary};
    border: 1px solid ${semantic.border.default};
    border-radius: 8px;
    font-weight: ${fonts.weight.medium};

    &:hover:not(:disabled) {
      background-color: ${semantic.bg.surfaceSubtle};
      border-color: ${semantic.border.default};
      color: ${semantic.text.primary};
    }

    &[data-selected] {
      background-color: ${colors.primary};
      border-color: ${colors.primary};
      color: ${colors.white};

      &:hover:not(:disabled) {
        background-color: ${colors.primary};
        border-color: ${colors.primary};
        color: ${colors.white};
        opacity: 0.85;
      }
    }
  }

  &[data-variant='icon'] {
    height: ${space[7]};
    padding: ${space[2]} ${space[4]};
    border-radius: ${layout.borderRadiusFull};
    background: ${semantic.bg.surfaceSubtle};
    color: ${semantic.text.primary};
    border: none;

    &:focus-visible {
      background: ${semantic.bg.surfaceStrong};
      color: ${semantic.text.primary};
    }

    &:disabled {
      background: ${semantic.bg.surfaceSubtle};
      color: ${semantic.text.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.surfaceStrong};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background: ${semantic.bg.primary};
      color: ${semantic.text.inverse};
    }
  }

  &[data-variant='danger'] {
    background-color: ${component.button.danger.enabled.bg};
    color: ${component.button.danger.enabled.text};
    border: 2px solid ${component.button.danger.enabled.bg};

    &:focus-visible {
      background-color: ${component.button.danger.focussed.bg};
      border-color: ${component.button.danger.focussed.bg};
      color: ${component.button.danger.focussed.text};
      outline-color: ${semantic.bg.urgencyStrong};
    }

    &:disabled {
      background-color: ${component.button.danger.disabled.bg};
      color: ${component.button.danger.disabled.text};
      border-color: ${component.button.danger.disabled.bg};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background-color: ${component.button.danger.hover.bg};
      border-color: ${component.button.danger.hover.bg};
      color: ${component.button.danger.hover.text};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background-color: ${component.button.danger.pressed.bg};
      border-color: ${component.button.danger.pressed.bg};
      color: ${component.button.danger.pressed.text};
    }
  }

  &[data-variant='icon-ghost'] {
    height: ${space[7]};
    padding: ${space[1]} ${space[4]};
    border-radius: ${layout.borderRadiusFull};
    background: transparent;
    color: ${semantic.text.primary};
    border: none;

    &:focus-visible {
      outline-offset: -2px;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.surfaceStrong};
      opacity: 1;
    }
  }

  &[data-variant='icon-overlay'] {
    height: ${space[7]};
    padding: ${space[1]} ${space[4]};
    border-radius: ${layout.borderRadiusFull};
    background: ${semantic.bg.overlayBlur};
    backdrop-filter: blur(${blur.lg});
    color: ${colors.white};
    border: none;

    &:focus-visible {
      background: ${semantic.bg.overlayBlurStrong};
      outline-color: ${colors.white};
      outline-offset: -2px;
    }

    &:disabled {
      color: ${semantic.icon.disabled};
      opacity: 1;
    }

    &:hover:not(:disabled) {
      background: ${semantic.bg.overlayBlurStrong};
      opacity: 1;
    }

    &:active:not(:disabled) {
      background: ${semantic.bg.overlayBlurStrong};
    }
  }
`;
